import { useEffect, useRef, useState } from 'react';
import { Sparkles, Search, Loader2, RefreshCw, MapPinned } from 'lucide-react';

const QUICK_PROMPTS = [
  'Dog-friendly tasting rooms in Sonoma with a casual patio vibe',
  'Romantic spots in Napa valley with a view for a sunset tasting',
  'Boutique, quiet tasting rooms that offer premium cheese pairings',
];

const RECOVERY_SUGGESTIONS = [
  'Wineries with food trucks',
  'Best wineries for a date',
  'Tuscany-style vineyards with farm-to-table dining',
];

// Fallback refinements when the backend hasn't returned `followUps` yet.
const FALLBACK_REFINEMENTS = [
  'Make the second one dog-friendly',
  'Swap one stop for somewhere with lunch on-site',
  'Tighten the radius and keep it walkable between stops',
  'Add a non-tasting break — coffee, a view, anything quiet',
  'Lean more upscale on the last stop for sunset',
];

// Typewriter animation tuning. Modern AI products (ChatGPT, Claude,
// Perplexity, v0) use a slow type → pause → erase → next-suggestion cycle
// to surface ideas without rushing the eye.
const TYPE_INTERVAL_MS = 55;     // per-character type speed
const ERASE_INTERVAL_MS = 25;    // per-character erase speed
const HOLD_AFTER_TYPE_MS = 2800; // pause once a suggestion is fully typed
const HOLD_AFTER_ERASE_MS = 350; // brief pause before the next suggestion starts

/**
 * Compact, white-card variant of the AI search input.
 *
 * Renders:
 *   - Subtle "● Active Chat Thread (Turn N)" indicator + "Reset Chat" link when a
 *     conversation is in progress (placed inside the card header — NOT as separate
 *     breadcrumb pills above the map).
 *   - Sparkle-prefixed text input with submit button.
 *   - Quick-prompt pills (hidden once the user is mid-conversation so the card
 *     stays focused on the active refinement).
 *   - Recovery suggestions panel after an OUT_OF_SCOPE rejection.
 */
const AiSearchForm = ({
  query,
  setQuery,
  isSearching,
  searchActive,
  rejectedQuery,
  setRejectedQuery,
  activeChatHistory,
  completedTurns: completedTurnsProp,
  turnCount,
  followUps,
  runSearch,
  handleResetChat,
  maxRadiusMiles,
  setMaxRadiusMiles,
}) => {
  const hasConversation = activeChatHistory.length > 0;
  // Derive locally as a fallback — hook now exports completedTurns directly
  const completedTurns = typeof completedTurnsProp === 'number'
    ? completedTurnsProp
    : Math.floor((activeChatHistory || []).length / 2);
  const atTurnCap = completedTurns >= 6;
  const inputRef = useRef(null);
  // Typewriter state. We animate a *placeholder* string instead of the
  // input value — keeps the input clean (user never sees the rotating
  // text become the submitted query) and matches the slick feel of ChatGPT
  // / Claude / Perplexity / v0 search prompts.
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  // The FULL suggestion currently being animated (not the partial typed
  // slice). When the user clicks Refine on an empty input, we treat the
  // visible suggestion as their intent and submit it instead of refusing
  // with the "type a few more words" toast.
  const currentSuggestionRef = useRef('');

  // Build the rotation pool: ONLY the backend's followUps for this trip.
  // These are the same suggestions the user can see as tappable chips in
  // the results panel — so the typewriter "previews" the very choices
  // already on offer (no fake hardcoded suggestions that don't exist as
  // buttons elsewhere).
  const rotationPool = (() => {
    const seen = new Set();
    const out = [];
    (Array.isArray(followUps) ? followUps : []).forEach((s) => {
      const t = (s || '').trim();
      if (!t) return;
      const k = t.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(t);
    });
    return out;
  })();
  const rotationKey = rotationPool.join('|');

  // Typewriter loop driven by setTimeout chaining. We use chained timeouts
  // (not a single setInterval) so the per-phase delays differ — type slow,
  // hold long, erase fast, hold short. Cancelled cleanly on unmount via the
  // returned cleanup. Pauses entirely when the user has typed their own
  // text (input has a value) so we never overwrite their edits.
  // Auto-clear the input as soon as a new turn completes so the typewriter
  // placeholder becomes visible. Without this, the input stays populated
  // with the user's last submitted query and the placeholder (where the
  // typewriter animates) is hidden behind the value.
  useEffect(() => {
    if (!hasConversation || isSearching) return;
    // Only clear when there IS a value to clear — otherwise we'd loop
    // re-running the effect on every render. The dependency on turnCount
    // means this fires exactly once per completed turn.
    if (query) setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConversation, isSearching, turnCount]);

  useEffect(() => {
    if (!hasConversation || isSearching) {
      // Reset typewriter when there's no conversation to suggest from
      // (initial load) or when a turn is currently being processed.
      setTypedPlaceholder('');
      return undefined;
    }
    if (rotationPool.length === 0) return undefined;

    let cancelled = false;
    let timer = null;
    let suggestionIdx = (turnCount || 1) % rotationPool.length;
    let charIdx = 0;
    let phase = 'typing'; // typing → holding → erasing → resting → typing(next)
    // Keep the ref in sync so onSubmit can grab the full text even
    // mid-animation.
    currentSuggestionRef.current = rotationPool[suggestionIdx];

    const step = () => {
      if (cancelled) return;
      const current = rotationPool[suggestionIdx];
      currentSuggestionRef.current = current;
      switch (phase) {
        case 'typing': {
          charIdx += 1;
          setTypedPlaceholder(current.slice(0, charIdx));
          if (charIdx >= current.length) {
            phase = 'holding';
            timer = window.setTimeout(step, HOLD_AFTER_TYPE_MS);
          } else {
            timer = window.setTimeout(step, TYPE_INTERVAL_MS);
          }
          break;
        }
        case 'holding': {
          phase = 'erasing';
          timer = window.setTimeout(step, ERASE_INTERVAL_MS);
          break;
        }
        case 'erasing': {
          charIdx -= 1;
          setTypedPlaceholder(current.slice(0, Math.max(0, charIdx)));
          if (charIdx <= 0) {
            phase = 'resting';
            timer = window.setTimeout(step, HOLD_AFTER_ERASE_MS);
          } else {
            timer = window.setTimeout(step, ERASE_INTERVAL_MS);
          }
          break;
        }
        case 'resting': {
          suggestionIdx = (suggestionIdx + 1) % rotationPool.length;
          charIdx = 0;
          phase = 'typing';
          timer = window.setTimeout(step, TYPE_INTERVAL_MS);
          break;
        }
        default:
          break;
      }
    };
    // Seed first frame immediately so the user sees motion right away.
    timer = window.setTimeout(step, TYPE_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConversation, isSearching, turnCount, rotationKey]);

  const onSubmit = (e) => {
    e?.preventDefault?.();
    // When the user submits with an empty input but the typewriter is
    // showing a suggestion, treat the visible suggestion as their intent
    // — this matches the user expectation "if I can see it, clicking
    // Refine should send it" (no need to manually copy the placeholder).
    const visibleSuggestion = currentSuggestionRef.current || '';
    const submitText = (query || '').trim() || visibleSuggestion.trim();
    runSearch(submitText);
  };

  return (
    <div className="w-full" data-testid="ai-search-form-wrap">
      {/* Subtle active-thread indicator (replaces the old turn-pill breadcrumb). */}
      {hasConversation && (
        <div
          className="mb-3 flex items-center justify-between gap-3"
          data-testid="ai-search-thread-indicator"
        >
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wine-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-wine-600" />
            </span>
            Active Trip
            <span
              className={`font-semibold tabular-nums ${completedTurns >= 5 ? 'text-amber-600' : 'text-gray-600'}`}
              data-testid="ai-turn-counter"
            >
              · Turn {Math.min(completedTurns, 6)} / 6
            </span>
          </span>
          <button
            type="button"
            onClick={handleResetChat}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-wine-700 hover:text-wine-900 underline decoration-dotted underline-offset-2 transition-colors"
            data-testid="ai-search-reset-link"
            aria-label="Reset chat and start a fresh conversation"
          >
            <RefreshCw className="w-3 h-3" />
            Reset Concierge
          </button>
        </div>
      )}

      {/* Turn cap reached — replace form with a friendly card */}
      {atTurnCap ? (
        <div
          className="rounded-xl border border-gold-200 bg-gradient-to-br from-gold-50 to-wine-50 p-4"
          data-testid="ai-turn-cap-panel"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-wine-600 to-wine-700 flex items-center justify-center mt-0.5">
              <Sparkles className="w-4 h-4 text-gold-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#1a1a2e] leading-snug">
                Great conversation! You've refined this trip 6 times.
              </p>
              <p className="text-xs text-gray-500 mt-1 leading-snug">
                Start fresh to plan a completely new wine country adventure.
              </p>
              <button
                type="button"
                onClick={handleResetChat}
                className="mt-3 inline-flex items-center gap-1.5 bg-wine-600 hover:bg-wine-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                data-testid="ai-turn-cap-reset-button"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      ) : (
      <form
        onSubmit={onSubmit}
        className={`relative group rounded-xl border-2 transition-colors bg-white ${
          hasConversation
            ? 'border-wine-500 ring-2 ring-wine-200 shadow-[0_0_0_4px_rgba(140,30,55,0.06)]'
            : 'border-[#1a1a2e]/10 focus-within:border-gold-400'
        }`}
        data-testid="ai-search-form"
      >
        {hasConversation && (
          <div className="flex items-center gap-1.5 px-3 sm:px-4 pt-2.5 -mb-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-wine-600">
              {completedTurns === 5
                ? 'Last refinement — make it count!'
                : 'Ask a follow-up — type to override the suggestion'}
            </span>
          </div>
        )}
        <div className="relative flex items-start gap-2 px-3 sm:px-4 py-3 w-full">
          <Sparkles className="text-wine-600 shrink-0 w-5 h-5 mt-1" />
          <textarea
            ref={inputRef}
            rows={2}
            value={query}
            maxLength={300}
            onChange={(e) => {
              // Hard-cap to 300 chars at input time so we never even
              // BUFFER an oversized query. Matches the backend's
              // max_length=300 on AISearchRequest.query so the server
              // never has to reject. Cost: ~75 input tokens — keeps
              // Haiku 4.5 calls in the ~$0.0001/turn range.
              const next = (e.target.value || '').slice(0, 300);
              setQuery(next);
              if (rejectedQuery) setRejectedQuery(null);
            }}
            onKeyDown={(e) => {
              // Enter submits. Shift+Enter inserts a newline for power users
              // who want to break their prompt into multiple bullet points.
              // We share the empty-input → use-visible-suggestion fallback
              // with the submit button so both surfaces behave the same.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                const visible = currentSuggestionRef.current || '';
                runSearch((query || '').trim() || visible.trim());
              }
            }}
            placeholder={
              hasConversation
                ? (typedPlaceholder || 'Refine your trip…')
                : 'Describe your ideal wine country trip plan vibe...'
            }
            // resize-none: lock the user out of grabbing the corner handle
            // (the rows={2} layout is intentional and matches the design).
            // leading-snug + py-0 keeps the textarea visually compact at 2 rows.
            className="flex-1 w-full bg-transparent text-[#1a1a2e] placeholder-gray-400 text-sm sm:text-base outline-none border-0 min-w-0 resize-none leading-snug py-0"
            aria-label="AI wine trip plan search"
            data-testid="ai-search-input"
          />
          <button
            type="submit"
            disabled={isSearching}
            className="shrink-0 self-start mt-0.5 inline-flex items-center gap-1.5 bg-[#1a1a2e] hover:bg-[#16213e] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg transition-all hover:scale-[1.02] active:scale-[0.97]"
            data-testid="ai-search-submit-button"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="hidden sm:inline">{hasConversation ? 'Refine' : 'Plan trip'}</span>
          </button>
        </div>

        {/* Live character counter — keeps the user honest about the 300-char
            limit. Turns amber at 80% (>=240) and red at 95% (>=285) so they
            see they're close before the cap hits. Anchored bottom-right of
            the form so it's visible without crowding the textarea. */}
        {query.length > 0 && (
          <div
            className={`absolute bottom-1.5 right-3 text-[10px] font-mono tabular-nums pointer-events-none transition-colors ${
              query.length >= 285 ? 'text-red-600 font-bold' :
              query.length >= 240 ? 'text-amber-600 font-semibold' :
              'text-gray-400'
            }`}
            data-testid="ai-search-char-counter"
            aria-live="polite"
          >
            {query.length} / 300
          </div>
        )}
      </form>
      )}

      {/* "Keep stops within X miles" — user-defined spacing constraint.
          Hidden when at turn cap since the form itself is replaced. */}
      {!atTurnCap && typeof setMaxRadiusMiles === 'function' && (() => {
        const RADIUS_STEPS = [0, 1, 2, 3, 5, 10, 20, 40, 60];
        const currentMiles = maxRadiusMiles || 0;
        // Snap the current persisted value back to the nearest step index so
        // older saved trips with arbitrary 25/50/75 values still slot in.
        const nearestIdx = RADIUS_STEPS.reduce(
          (best, v, i) =>
            Math.abs(v - currentMiles) < Math.abs(RADIUS_STEPS[best] - currentMiles) ? i : best,
          0,
        );
        return (
        <div className="mt-2.5 px-1" data-testid="ai-radius-control">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
              <MapPinned className="w-3 h-3 text-wine-600" />
              Stops within
            </span>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center min-w-[3.5rem] px-2 py-0.5 rounded-md bg-wine-50 border border-wine-200 text-wine-800 text-xs font-bold tabular-nums"
                data-testid="ai-radius-value-badge"
                aria-live="polite"
              >
                {currentMiles > 0 ? `${currentMiles} mi` : 'No limit'}
              </span>
            </div>
          </div>
          <input
            type="range"
            min="0"
            max={RADIUS_STEPS.length - 1}
            step="1"
            list="ai-radius-ticks"
            value={nearestIdx}
            onChange={(e) => {
              const idx = parseInt(e.target.value, 10);
              const miles = RADIUS_STEPS[idx] || 0;
              setMaxRadiusMiles(miles > 0 ? miles : null);
            }}
            className="w-full mt-1.5 accent-wine-600 cursor-pointer"
            data-testid="ai-radius-slider"
            aria-label="Maximum miles between stops"
            aria-valuetext={currentMiles > 0 ? `${currentMiles} miles` : 'No limit'}
          />
          <datalist id="ai-radius-ticks">
            {RADIUS_STEPS.map((v, i) => (
              <option key={i} value={i} label={v === 0 ? '∞' : String(v)} />
            ))}
          </datalist>
          <div
            className="grid gap-0 text-[9px] text-gray-400 -mt-0.5 select-none tabular-nums"
            style={{ gridTemplateColumns: `repeat(${RADIUS_STEPS.length}, minmax(0, 1fr))` }}
          >
            {RADIUS_STEPS.map((v, i) => (
              <span
                key={i}
                className={`${
                  i === 0 ? 'text-left' : i === RADIUS_STEPS.length - 1 ? 'text-right' : 'text-center'
                } ${i === nearestIdx ? 'text-wine-700 font-bold' : ''}`}
              >
                {v === 0 ? '∞' : v}
              </span>
            ))}
          </div>
        </div>
        );
      })()}

      {/* Quick-prompt pills — hidden while a conversation is active so the card stays focused. */}
      {!atTurnCap && !hasConversation && !searchActive && (
        <div className="mt-4 space-y-2" data-testid="ai-search-prompts">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 mb-2">
            Try one of these
          </div>
          {QUICK_PROMPTS.map((p, i) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setQuery(p);
                runSearch(p);
              }}
              className="group/pill w-full text-left text-xs sm:text-sm text-[#1a1a2e] bg-gray-50 hover:bg-wine-600 hover:text-white border border-gray-200 hover:border-wine-600 rounded-lg px-3.5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-md flex items-center gap-2"
              data-testid={`ai-search-prompt-${i}`}
            >
              <Sparkles className="w-3.5 h-3.5 text-gold-500 group-hover/pill:text-gold-300 shrink-0" />
              <span className="flex-1">{p}</span>
            </button>
          ))}
        </div>
      )}

      {/* Recovery panel — only after an OUT_OF_SCOPE rejection and not at turn cap. */}
      {!atTurnCap && rejectedQuery && (
        <div
          className="mt-4 rounded-xl border border-wine-200 bg-wine-50/70 p-3.5"
          data-testid="ai-search-recovery-panel"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 text-wine-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-[#1a1a2e] font-medium leading-snug">
                We didn&apos;t quite get that. Try a wine-country vibe instead:
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {RECOVERY_SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setQuery(s);
                      runSearch(s);
                    }}
                    className="text-[11px] sm:text-xs text-wine-700 bg-white hover:bg-wine-600 hover:text-white border border-wine-200 hover:border-wine-600 rounded-full px-3 py-1 transition-all hover:-translate-y-0.5 hover:shadow-sm"
                    data-testid={`ai-search-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSearchForm;
